import React, { useEffect, useMemo, useState } from "react";
import { Timestamp, doc, serverTimestamp, updateDoc } from "firebase/firestore";
import { getBlob, ref as stRef } from "firebase/storage";
import { db, storage } from "../../firebase";
import { useVenue } from "../VenueProvider";
import { showAlert } from "../../alertService";
import { BITACORA_ACTIONS, logBitacora } from "../../utils/bitacora";
import {
  saldosAFavor,
  totalSaldoAFavor,
  tieneSaldoAFavorActivo,
} from "../../utils/costos";
import { fmtMXNInput, normalizeMoneyInput } from "../../utils/money";
import MetodoPagoField from "../shared/MetodoPagoField";
import { Badge } from "../badge";
import DialogoConfirmacion, { Resumen, Fila } from "../shared/DialogoConfirmacion";
import { BTN_SECUNDARIO_SM, INPUT, Vacio, fmtDate, fmtMoney } from "./shared";
import { ArrowRightCircleIcon, BanknotesIcon } from "@heroicons/react/24/outline";

// ---------- Saldo a favor ----------
//
// Dinero que YA le pagaste por un evento que después se canceló. El pago
// existió (sigue en el ledger como gasto); lo que cambia es que ahora el
// proveedor te lo debe, o te lo cobra en el siguiente evento. Las dos
// salidas:
//   - Aplicar a otro evento: el costo se MUDA a ese evento. Un solo gasto,
//     una sola fecha de pago; nada se duplica ni se vuelve a capturar.
//   - Devuelto: el proveedor regresó el dinero (todo o parte). Entra al
//     ledger como devolución con su fecha.
// Ver planCancelacionCostos en utils/costos.js y la cancelación en
// EventPage.

const ESTADO = {
  activo: { label: "Pendiente", color: "amber" },
  aplicado: { label: "Aplicado", color: "emerald" },
  devuelto: { label: "Devuelto", color: "teal" },
};

const hoyInput = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const aDate = (v) => (v?.toDate ? v.toDate() : v?.seconds ? new Date(v.seconds * 1000) : v ? new Date(v) : null);

const SaldoAFavorTab = ({ proveedor, venueId, costos, events, navigate, userInfo, user, soloLectura = false }) => {
  const [aplicando, setAplicando] = useState(null); // costo
  const [devolviendo, setDevolviendo] = useState(null); // costo
  const [eventoDestino, setEventoDestino] = useState("");
  const [montoDevuelto, setMontoDevuelto] = useState("");
  const [fechaDevolucion, setFechaDevolucion] = useState(hoyInput());
  const [metodoDevolucion, setMetodoDevolucion] = useState("");
  const [notas, setNotas] = useState("");
  const [saving, setSaving] = useState(false);

  const lista = useMemo(() => saldosAFavor(costos, { proveedorId: proveedor?.id }), [costos, proveedor?.id]);

  // Comprobantes: cuelgan del evento ORIGEN (archivos con linkedTo.kind ===
  // "costo"), no del costo. Se cargan una vez por evento origen para poder
  // enseñar "aquí está la transferencia" desde esta misma ficha — que es el
  // punto de la sección: si el proveedor dice que no se le pagó, aquí consta
  // cuándo, cómo, para qué evento era y a cuál se aplicó.
  const { loadEventArchivos, metodosPagoConDefaults: metodosPago } = useVenue();
  const [comprobantes, setComprobantes] = useState({}); // costoId -> archivo
  const [descargando, setDescargando] = useState(null);
  const eventosOrigen = useMemo(
    () => [...new Set(lista.map((c) => c.saldoAFavor?.eventId).filter(Boolean))].sort().join("|"),
    [lista]
  );
  useEffect(() => {
    let vivo = true;
    const ids = eventosOrigen ? eventosOrigen.split("|") : [];
    if (!ids.length) {
      setComprobantes({});
      return undefined;
    }
    (async () => {
      const map = {};
      for (const eventId of ids) {
        try {
          const archivos = await loadEventArchivos("default", eventId);
          archivos.forEach((a) => {
            if (a?.linkedTo?.kind === "costo" && a.linkedTo.id && a.storagePath) {
              map[a.linkedTo.id] = a;
            }
          });
        } catch (err) {
          console.warn("Saldo a favor · comprobantes:", err);
        }
      }
      if (vivo) setComprobantes(map);
    })();
    return () => {
      vivo = false;
    };
  }, [eventosOrigen, loadEventArchivos]);

  const descargar = async (costoId) => {
    const archivo = comprobantes[costoId];
    if (!archivo?.storagePath) return;
    setDescargando(costoId);
    try {
      const blob = await getBlob(stRef(storage, archivo.storagePath));
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = archivo.filename || archivo.label || "comprobante";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      showAlert("No se pudo descargar el comprobante.", "error");
    } finally {
      setDescargando(null);
    }
  };
  const total = useMemo(() => totalSaldoAFavor(costos, { proveedorId: proveedor?.id }), [costos, proveedor?.id]);

  // Solo eventos vivos, del más próximo al más lejano. El evento del que
  // salió el saldo no es destino (está cancelado).
  const destinos = useMemo(
    () =>
      (events || [])
        .filter((e) => e && e.eventType === "event" && e.status !== "cancelado")
        .sort((a, b) => (aDate(b.startTime)?.getTime() || 0) - (aDate(a.startTime)?.getTime() || 0)),
    [events]
  );

  const abrirAplicar = (c) => {
    setEventoDestino("");
    setNotas("");
    setAplicando(c);
  };
  const abrirDevolver = (c) => {
    setMontoDevuelto(fmtMXNInput(String(c.monto || "")));
    setFechaDevolucion(hoyInput());
    setMetodoDevolucion("");
    setNotas("");
    setDevolviendo(c);
  };
  const cerrar = () => {
    setAplicando(null);
    setDevolviendo(null);
  };

  const ref = (c) => doc(db, "venues", venueId, "costos", c.id);

  const aplicar = async () => {
    if (!aplicando || !eventoDestino) return;
    const destino = destinos.find((e) => e.id === eventoDestino);
    if (!destino) return;
    setSaving(true);
    try {
      const ahora = Timestamp.now();
      await updateDoc(ref(aplicando), {
        eventId: destino.id,
        // Deja de ser "de la cotización" del evento cancelado: en el destino
        // cuenta como costo ya capturado (no se duplica) y nadie lo revalúa.
        origen: "saldo_a_favor",
        origenAnterior: aplicando.origen || null,
        saldoAFavor: {
          ...(aplicando.saldoAFavor || {}),
          estado: "aplicado",
          aplicadoEventId: destino.id,
          aplicadoEventoNombre: destino.nombre || "",
          resueltoAt: ahora,
          notas: notas.trim() || null,
        },
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      });
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.COSTO_SALDO_FAVOR_APLICAR,
          entity: "costo",
          entityId: aplicando.id,
          entityName: aplicando.concepto || "",
          details: {
            proveedorId: proveedor.id,
            proveedorNombre: proveedor.nombre,
            monto: Number(aplicando.monto) || 0,
            deEvento: aplicando.saldoAFavor?.eventoNombre || null,
            aEvento: destino.nombre || "",
            aEventoId: destino.id,
          },
        },
        userInfo,
        user
      );
      showAlert(`Saldo aplicado a ${destino.nombre || "el evento"}`, "success");
      cerrar();
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  const devolver = async () => {
    if (!devolviendo) return;
    const monto = Number(normalizeMoneyInput(montoDevuelto)) || 0;
    const tope = Number(devolviendo.monto) || 0;
    if (monto <= 0 || monto > tope) {
      showAlert(`El monto devuelto debe ser mayor a 0 y hasta ${fmtMoney(tope)}.`, "error");
      return;
    }
    const fecha = fechaDevolucion ? new Date(`${fechaDevolucion}T12:00:00`) : new Date();
    if (isNaN(fecha)) {
      showAlert("Fecha de devolución inválida.", "error");
      return;
    }
    setSaving(true);
    try {
      await updateDoc(ref(devolviendo), {
        saldoAFavor: {
          ...(devolviendo.saldoAFavor || {}),
          estado: "devuelto",
          montoDevuelto: monto,
          fechaDevolucion: Timestamp.fromDate(fecha),
          metodoDevolucion: metodoDevolucion || null,
          resueltoAt: Timestamp.now(),
          notas: notas.trim() || null,
        },
        updatedAt: serverTimestamp(),
        updatedBy: user?.uid || null,
      });
      logBitacora(
        venueId,
        {
          action: BITACORA_ACTIONS.COSTO_SALDO_FAVOR_DEVUELTO,
          entity: "costo",
          entityId: devolviendo.id,
          entityName: devolviendo.concepto || "",
          details: {
            proveedorId: proveedor.id,
            proveedorNombre: proveedor.nombre,
            monto: Number(devolviendo.monto) || 0,
            montoDevuelto: monto,
            metodo: metodoDevolucion || null,
            deEvento: devolviendo.saldoAFavor?.eventoNombre || null,
          },
        },
        userInfo,
        user
      );
      showAlert("Devolución registrada", "success");
      cerrar();
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/60 p-4">
        <div className="text-xs uppercase tracking-wide font-semibold text-zinc-400">Pendiente de resolver</div>
        <div className={`mt-1 text-2xl font-bold ${total > 0 ? "text-amber-600" : "text-zinc-900 dark:text-white"}`}>
          {fmtMoney(total)}
        </div>
        <p className="mt-1 text-xs text-zinc-500">
          Lo que ya le pagaste por eventos cancelados. Aplícalo a otro evento o registra que te lo devolvió.
        </p>
      </div>

      {lista.length === 0 ? (
        <Vacio>Sin saldos a favor con este proveedor.</Vacio>
      ) : (
        <div className="rounded-xl border border-zinc-200 dark:border-zinc-700/60 divide-y divide-zinc-100 dark:divide-zinc-700/60 bg-white dark:bg-zinc-800/60">
          {lista.map((c) => {
            const s = c.saldoAFavor || {};
            const est = ESTADO[s.estado] || ESTADO.activo;
            return (
              <div key={c.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-zinc-900 dark:text-white truncate">
                    {c.concepto || "Costo"}
                  </div>
                  <div className="text-xs text-zinc-500 space-y-0.5">
                    <div>
                      Pagado el {fmtDate(c.fechaPago || c.fecha) || "—"}
                      {c.metodo ? ` por ${c.metodo}` : ""} · para{" "}
                      {s.eventId ? (
                        <button
                          type="button"
                          className="hover:underline"
                          onClick={() => navigate(`/dashboard/eventos/${s.eventId}`)}
                        >
                          {s.eventoNombre || "evento cancelado"}
                        </button>
                      ) : (
                        "evento cancelado"
                      )}{" "}
                      (cancelado)
                    </div>
                    {s.estado === "aplicado" && (
                      <div>
                        Aplicado{s.resueltoAt ? ` el ${fmtDate(s.resueltoAt)}` : ""} a{" "}
                        {s.aplicadoEventId ? (
                          <button
                            type="button"
                            className="font-medium hover:underline"
                            style={{ color: "#1b4d2e" }}
                            onClick={() => navigate(`/dashboard/eventos/${s.aplicadoEventId}`)}
                          >
                            {s.aplicadoEventoNombre || "otro evento"}
                          </button>
                        ) : (
                          "otro evento"
                        )}
                        {s.notas ? ` — ${s.notas}` : ""}
                      </div>
                    )}
                    {s.estado === "devuelto" && (
                      <div>
                        Devuelto {fmtMoney(s.montoDevuelto ?? c.monto)} el {fmtDate(s.fechaDevolucion) || "—"}
                        {s.metodoDevolucion ? ` por ${s.metodoDevolucion}` : ""}
                        {s.notas ? ` — ${s.notas}` : ""}
                      </div>
                    )}
                    {/* Solo lectura (P-FRA-13): se nombra el comprobante, sin descarga. */}
                    {comprobantes[c.id] && soloLectura && (
                      <div className="text-zinc-500">
                        Comprobante adjunto
                        {comprobantes[c.id].filename ? ` · ${comprobantes[c.id].filename}` : ""}
                      </div>
                    )}
                    {comprobantes[c.id] && !soloLectura && (
                      <div>
                        <button
                          type="button"
                          className="hover:underline"
                          style={{ color: "#1b4d2e" }}
                          disabled={descargando === c.id}
                          onClick={() => descargar(c.id)}
                        >
                          {descargando === c.id ? "Descargando…" : "Descargar comprobante"}
                          {comprobantes[c.id].filename ? ` · ${comprobantes[c.id].filename}` : ""}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
                <div className="font-semibold text-zinc-900 dark:text-white whitespace-nowrap">
                  {fmtMoney(c.monto)}
                </div>
                <Badge color={est.color}>{est.label}</Badge>
                {tieneSaldoAFavorActivo(c) && !soloLectura && (
                  <div className="flex gap-2">
                    <button type="button" className={BTN_SECUNDARIO_SM} onClick={() => abrirAplicar(c)}>
                      Aplicar a otro evento
                    </button>
                    <button type="button" className={BTN_SECUNDARIO_SM} onClick={() => abrirDevolver(c)}>
                      Me lo devolvió
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Aplicar a otro evento */}
      <DialogoConfirmacion
        open={!!aplicando}
        onClose={cerrar}
        titulo="Aplicar saldo a otro evento"
        subtitulo={proveedor?.nombre || ""}
        icono={ArrowRightCircleIcon}
        saving={saving}
        cancelarLabel="Cancelar"
        confirmar={{
          label: "Aplicar",
          savingLabel: "Aplicando…",
          onClick: aplicar,
          disabled: !eventoDestino,
        }}
      >
        <p>
          El costo se muda al evento que elijas: cuenta como gasto de ese evento,
          ya pagado, sin volver a capturarlo.
        </p>
        <Resumen>
          <Fila tono="teal" monto={fmtMoney(aplicando?.monto)}>
            {aplicando?.concepto || "Costo"}
          </Fila>
          <Fila tono="zinc">
            De {aplicando?.saldoAFavor?.eventoNombre || "evento cancelado"} · pagado el{" "}
            {fmtDate(aplicando?.fechaPago || aplicando?.fecha) || "—"}
          </Fila>
        </Resumen>
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">Evento destino</label>
          <select
            className={INPUT}
            value={eventoDestino}
            onChange={(e) => setEventoDestino(e.target.value)}
          >
            <option value="">Elige un evento…</option>
            {destinos.map((e) => (
              <option key={e.id} value={e.id}>
                {(e.nombre || "Sin nombre") + " · " + (fmtDate(e.startTime) || "")}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">Notas (opcional)</label>
          <input
            className={INPUT}
            value={notas}
            onChange={(e) => setNotas(e.target.value)}
            placeholder="Ej. lo tomó a cuenta de la boda de marzo"
          />
        </div>
      </DialogoConfirmacion>

      {/* Devuelto */}
      <DialogoConfirmacion
        open={!!devolviendo}
        onClose={cerrar}
        titulo="Registrar devolución"
        subtitulo={proveedor?.nombre || ""}
        icono={BanknotesIcon}
        saving={saving}
        cancelarLabel="Cancelar"
        confirmar={{
          label: "Registrar devolución",
          savingLabel: "Guardando…",
          onClick: devolver,
        }}
      >
        <p>
          {proveedor?.nombre || "El proveedor"} te regresó dinero de este costo. Entra
          a Cuentas como devolución en la fecha que indiques.
        </p>
        <Resumen>
          <Fila tono="teal" monto={fmtMoney(devolviendo?.monto)}>
            {devolviendo?.concepto || "Costo"}
          </Fila>
          <Fila tono="zinc">
            De {devolviendo?.saldoAFavor?.eventoNombre || "evento cancelado"} · pagado el{" "}
            {fmtDate(devolviendo?.fechaPago || devolviendo?.fecha) || "—"}
          </Fila>
        </Resumen>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Monto devuelto</label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-zinc-400">$</span>
              <input
                className={`${INPUT} pl-7`}
                inputMode="decimal"
                value={montoDevuelto}
                onChange={(e) => setMontoDevuelto(fmtMXNInput(e.target.value))}
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-zinc-500 mb-1">Fecha de devolución</label>
            <input
              type="date"
              className={INPUT}
              value={fechaDevolucion}
              onChange={(e) => setFechaDevolucion(e.target.value)}
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">Cómo te lo devolvió</label>
          <MetodoPagoField
            value={metodoDevolucion}
            onChange={setMetodoDevolucion}
            metodosPago={metodosPago}
            className={INPUT}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-zinc-500 mb-1">Notas (opcional)</label>
          <input className={INPUT} value={notas} onChange={(e) => setNotas(e.target.value)} />
        </div>
      </DialogoConfirmacion>
    </div>
  );
};

export default SaldoAFavorTab;
